import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// === Almacenamiento de sesión ===
// Usamos sessionStorage para el token y datos del usuario: al cerrar la
// pestaña/ventana, la sesión se borra automáticamente y obliga a re-login.
const SS = window.sessionStorage;

// Limpieza one-shot de tokens viejos en localStorage para evitar persistencia
// de sesión entre cierres de navegador (problema reportado).
try {
  ['token', 'cached_user', 'portal_token', 'activeRole', 'admin_backup',
   'perfiles_multi', 'active_gerencia'].forEach(k => localStorage.removeItem(k));
} catch { /* ignore */ }

// Set axios default header synchronously on module load to avoid race conditions
// between child useEffects (which fire first) and the AuthProvider's useEffect.
const _initialToken = SS.getItem('token');
if (_initialToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${_initialToken}`;
}

// Global 401 interceptor: if the session expires on ANY api call, clear storage
// and send the user to /login (avoids pages stuck in a spinner when token expired).
let _redirecting = false;
axios.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    if (status === 401 && url.includes('/api/') && !url.includes('/auth/login') && !_redirecting) {
      _redirecting = true;
      try {
        SS.removeItem('token');
        SS.removeItem('cached_user');
        SS.removeItem('portal_token');
        SS.removeItem('activeRole');
        SS.removeItem('admin_backup');
        SS.removeItem('perfiles_multi');
        SS.removeItem('active_gerencia');
        delete axios.defaults.headers.common['Authorization'];
      } catch { /* ignore */ }
      // Redirect full page so all stale state resets
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?reason=expired';
      }
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { const c = SS.getItem('cached_user'); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [loading, setLoading] = useState(() => !SS.getItem('cached_user'));
  const [token, setToken] = useState(SS.getItem('token'));
  const [impersonating, setImpersonating] = useState(false);
  const [originalAdmin, setOriginalAdmin] = useState(null);
  const [activeRole, setActiveRole] = useState(SS.getItem('activeRole') || null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Restore impersonation state on mount
  useEffect(() => {
    const saved = SS.getItem('admin_backup');
    if (saved) {
      try {
        setOriginalAdmin(JSON.parse(saved));
        setImpersonating(true);
      } catch { /* ignore */ }
    }
  }, []);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, { timeout: 10000 });
      // Preservar el tipo seleccionado del perfil (ej. 'key_user') si el cached_user ya lo tiene
      let nextUser = res.data;
      try {
        const cached = JSON.parse(SS.getItem('cached_user') || 'null');
        if (cached?.tipo === 'key_user' && nextUser.key_user) {
          nextUser = { ...nextUser, tipo: 'key_user' };
        }
      } catch { /* ignore */ }
      setUser(nextUser);
      SS.setItem('cached_user', JSON.stringify(nextUser));
    } catch (error) {
      console.error('Error fetching user:', error);
      // Only logout on 401 (invalid token). On network/timeout, keep cached user if any.
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    // Si requiere seleccion, devolver datos al caller; no establecer token aun.
    if (res.data?.requiere_seleccion) {
      // Guardamos los perfiles para permitir "Cambiar de perfil"
      try { SS.setItem('perfiles_multi', JSON.stringify(res.data.perfiles)); } catch {}
      return { requiere_seleccion: true, perfiles: res.data.perfiles };
    }
    // Login simple → limpiamos cualquier perfiles_multi previo
    SS.removeItem('perfiles_multi');
    const { token: newToken, user: userData } = res.data;
    SS.setItem('token', newToken);
    SS.setItem('cached_user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setLoading(false);
    SS.removeItem('activeRole');
    setActiveRole(null);
    return userData;
  };

  const applyPerfil = ({ token: newToken, user: userData }) => {
    SS.setItem('token', newToken);
    SS.setItem('cached_user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setLoading(false);
    SS.removeItem('activeRole');
    setActiveRole(null);
    return userData;
  };

  // Devuelve los perfiles disponibles para el usuario actual (si hay multi)
  const getPerfilesMulti = () => {
    try {
      const raw = SS.getItem('perfiles_multi');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const hasMultiplePerfiles = () => {
    const p = getPerfilesMulti();
    return p && Object.keys(p).length > 1;
  };

  const selectRole = (role) => {
    setActiveRole(role);
    SS.setItem('activeRole', role);
  };

  const logout = () => {
    SS.removeItem('token');
    SS.removeItem('cached_user');
    SS.removeItem('portal_token');
    SS.removeItem('admin_backup');
    SS.removeItem('activeRole');
    SS.removeItem('perfiles_multi');
    SS.removeItem('active_gerencia');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setImpersonating(false);
    setOriginalAdmin(null);
    setActiveRole(null);
  };

  // Cambiar de perfil sin perder sesion: restaura perfiles_multi para que
  // SeleccionarPerfil maneje la nueva selección.
  const switchPerfil = () => {
    const perfiles = getPerfilesMulti();
    if (!perfiles) return false;
    sessionStorage.setItem('perfiles_disponibles', JSON.stringify(perfiles));
    return true;
  };

  // Cambia directamente al perfil indicado (key: 'trabajador' | 'ese' | 'capacitador' | 'planificador' | 'key_user')
  // Devuelve true si tuvo éxito; false si ese perfil no está disponible.
  const switchToPerfil = (key) => {
    const perfiles = getPerfilesMulti();
    if (!perfiles?.[key]) return false;
    applyPerfil(perfiles[key]);
    if (key !== 'key_user') localStorage.removeItem('active_gerencia');
    return true;
  };

  const impersonate = async (personaId) => {
    const backup = { token, user };
    SS.setItem('admin_backup', JSON.stringify(backup));
    const res = await axios.post(`${API}/auth/impersonate/${personaId}`);
    const { token: workerToken, user: workerUser } = res.data;
    SS.setItem('token', workerToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${workerToken}`;
    setOriginalAdmin(backup);
    setImpersonating(true);
    setToken(workerToken);
    setUser(workerUser);
  };

  const exitImpersonation = () => {
    if (!originalAdmin) return;
    SS.setItem('token', originalAdmin.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${originalAdmin.token}`;
    SS.removeItem('admin_backup');
    setToken(originalAdmin.token);
    setUser(originalAdmin.user);
    setImpersonating(false);
    setOriginalAdmin(null);
  };

  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  const WARNING_BEFORE = 30 * 1000; // 30 seconds warning
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  useEffect(() => {
    if (!token) return;
    let logoutTimer;
    let warningTimer;
    let lastReset = Date.now();
    const resetTimer = () => {
      const now = Date.now();
      if (now - lastReset < 1000) return; // throttle to max 1 reset/sec
      lastReset = now;
      clearTimeout(logoutTimer);
      clearTimeout(warningTimer);
      setShowSessionWarning(false);
      warningTimer = setTimeout(() => { setShowSessionWarning(true); }, INACTIVITY_TIMEOUT - WARNING_BEFORE);
      logoutTimer = setTimeout(() => { setShowSessionWarning(false); logout(); }, INACTIVITY_TIMEOUT);
    };
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click', 'mousemove'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      clearTimeout(logoutTimer);
      clearTimeout(warningTimer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [token]);

  const isAdmin = () => user?.rol === 'administrador';
  const isTrabajador = () => user?.tipo === 'trabajador' || user?.rol === 'trabajador';
  const isCapacitador = () => user?.rol === 'capacitador';
  const isEse = () => user?.rol === 'ese';
  const isPlanificador = () => user?.es_planificador === true || user?.rol === 'planificador';
  const needsRoleSelection = () => !activeRole && !!user && user?.rol !== 'administrador' && user?.rol !== 'capacitador' && user?.rol !== 'ese' && user?.rol !== 'pases_visita';

  return (
    <AuthContext.Provider value={{ 
      user, loading, login, applyPerfil, logout, isAdmin, isTrabajador, isCapacitador, isEse, isPlanificador, token,
      hasMultiplePerfiles, switchPerfil, switchToPerfil, getPerfilesMulti,
      impersonate, exitImpersonation, impersonating,
      activeRole, selectRole, needsRoleSelection, showSessionWarning,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
